/**
 * StatusPill — the canonical rendering of operational state.
 *
 * The status ramp (healthy/warn/critical/info) is reserved EXCLUSIVELY for
 * operational state (design rule). This component is the only sanctioned place
 * those colors appear as a label, so brand violet is never confused with health.
 */
import type { ReactNode } from "react";
import type { HealthState, DeviceStatus } from "@/types/domain";
import { cn } from "@/lib/cn";

const LABELS: Record<string, string> = {
  healthy: "Healthy",
  warn: "Degraded",
  critical: "Critical",
  info: "Info",
  unknown: "Unknown",
};

interface StatusPillProps {
  status: HealthState | "info";
  /** Optional label override (e.g. "Offline 46m"). */
  children?: ReactNode;
  className?: string;
  /**
   * Render the leading dot as a RING instead of a filled disc.
   *
   * This is an information encoding, not decoration: filled = we have had contact with this
   * device; hollow = we never have. One glyph rule across the whole status family, so
   * "never seen" is legible at a glance even in a dense list where the color is a muted grey.
   */
  hollow?: boolean;
  title?: string;
}

export function StatusPill({ status, children, className, hollow, title }: StatusPillProps) {
  // "unknown" has no ramp color — render as a neutral, muted chip.
  const dataStatus = status === "unknown" ? undefined : status;
  return (
    <span
      className={cn(
        "md-status",
        status === "unknown" && "bg-surface-2 text-ink-muted",
        className,
      )}
      data-status={dataStatus}
      title={title}
    >
      <span
        aria-hidden
        className={cn(
          "inline-block h-1.5 w-1.5 rounded-full",
          // border-current + transparent fill gives the ring its color from the pill's own
          // text color, so it tracks the ramp (and both themes) with no extra tokens.
          hollow ? "border border-current bg-transparent" : "bg-current",
        )}
      />
      {children ?? LABELS[status] ?? status}
    </span>
  );
}

// Device lifecycle status -> the reserved status ramp + a precise operational label. Separates
// "degraded" (reachable, but has an open alert) from "offline" (unreachable) -- the fidelity the
// single online/offline boolean lacked.
//
// "dormant" is the newest and most consequential of these. It means REGISTERED BUT NEVER
// CONTACTED, and it is deliberately NOT on the critical ramp: a device nobody has plugged in
// yet is not an outage. On 2026-09-10 an ECS2100 that had never been uCentral-managed sat at
// OFFLINE indefinitely and read as a down device, and time went into chasing an outage that did
// not exist. Grey + a hollow dot says "we have never heard from this" without competing for
// attention with the devices that are actually down.
const DEVICE_STATUS: Record<
  DeviceStatus,
  { ramp: HealthState | "info"; label: string; hollow?: boolean; title?: string }
> = {
  online: { ramp: "healthy", label: "Online" },
  degraded: { ramp: "warn", label: "Degraded" },
  offline: { ramp: "critical", label: "Offline" },
  provisioning: { ramp: "info", label: "Provisioning" },
  dormant: {
    ramp: "unknown",
    label: "Never seen",
    hollow: true,
    title: "Registered but has never contacted the controller — not an outage.",
  },
};

export function DeviceStatusPill({ status, className }: { status?: DeviceStatus; className?: string }) {
  const s = status ? DEVICE_STATUS[status] : null;
  if (!s) return <StatusPill status="unknown" className={className} />;
  return (
    <StatusPill status={s.ramp} className={className} hollow={s.hollow} title={s.title}>
      {s.label}
    </StatusPill>
  );
}
